import React from "react";

import { useNavigate } from "react-router-dom";

import _ from "lodash";

import ActionsTableCell from "commons/components/ActionsTableCell";

import useAlertConfirm, { AlertConfirmHookType } from "commons/hooks/useAlertConfirm";
import { DataTableHook } from "commons/hooks/useDataTable";
import useDataTableWithMemory from "commons/hooks/useDataTableWithMemory";
import { StaticTableFilterHook } from "commons/hooks/useStaticTableFilter";
import useStaticTableFilterWithMemory from "commons/hooks/useStaticTableFilterWithMemory";
import useTableFilterWithMemoryAndButtons, {
    TableFilterWithMemoryAndButtonsHook,
} from "commons/hooks/useTableFilterWithMemoryAndButtons";

import {
    Column,
    FilterFieldCollection,
    FilterOperation,
    FilterType,
    LoadTableDataInterface,
} from "commons/types/table";

import { calculateRows } from "commons/ui/datatable/TableRows";

import { makeUrl } from "commons/utils/http";

import useProductApi from "hooks/api/private/useProductApi";
import useSnackbar from "hooks/useSnackbar";

import { ProductListItem } from "types/api/product";

export type ProductsListHook = {
    navigateToNewProduct: () => void;
    onSearchFilterChange: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void;
    filterSearchHook: TableFilterWithMemoryAndButtonsHook;
    dataTableHook: DataTableHook<ProductListItem>;
    staticFilterHook: StaticTableFilterHook;
    alertConfirmHook: AlertConfirmHookType;
};

function useProductsList(): ProductsListHook {
    const navigate = useNavigate();

    const { getAllProduct, deleteProduct, duplicateProduct } = useProductApi();

    const onEditClick = React.useCallback(
        (row: ProductListItem) => {
            navigate(makeUrl(":productId/modifica", { productId: row.id }));
        },
        [navigate],
    );

    const navigateToNewProduct = React.useCallback(() => {
        navigate("nuovo");
    }, [navigate]);

    const memoizedFieldsForSearch = React.useMemo<FilterFieldCollection>(
        () => ({
            search: {
                operation: FilterOperation.Or,
                fields: {
                    name: {
                        key: "products.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    categories_language: {
                        key: "categories_language.name",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                    year: {
                        key: "products.year",
                        operation: FilterOperation.Like,
                        defaultValue: "",
                    },
                },
            },
        }),
        [],
    );

    const filterSearchHook = useTableFilterWithMemoryAndButtons(
        "product-list-search",
        memoizedFieldsForSearch,
    );

    const { setForm: setFiltersForm } = filterSearchHook;

    const onSearchFilterChange = React.useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setFiltersForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                const value = "" + event.target.value;

                newForm.search.name = value;
                newForm.search.year = value;

                return newForm;
            }),
        [setFiltersForm],
    );

    const memoizedFieldsForStaticFilters = React.useMemo(
        () => ({
            type: {
                key: "categories.type",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            front_visible: {
                key: "products.front_visible",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            reserved_area_visible: {
                key: "products.reserved_area_visible",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
            is_new: {
                key: "products.is_new",
                operation: FilterOperation.Eq,
                defaultValue: "",
            },
        }),
        [],
    );

    const staticFilterHook = useStaticTableFilterWithMemory(
        "product-list-static",
        memoizedFieldsForStaticFilters,
    );

    const { filters: searchFilters } = filterSearchHook;
    const { filters: staticFilters } = staticFilterHook;

    const filters: FilterType[] = React.useMemo(
        () => [...searchFilters, ...staticFilters],
        [searchFilters, staticFilters],
    );

    const alertConfirmHook = useAlertConfirm({
        title: "Elimina Prodotto",
        cancelButtonLabel: "Annulla",
        confirmButtonLabel: "Conferma",
    });

    const { enqueueSuccessSnackbar, enqueueErrorSnackbar } = useSnackbar();

    const openConfirmAlert = React.useMemo(() => alertConfirmHook.open, [alertConfirmHook.open]);

    const deleteProductButton = React.useCallback(
        (row: ProductListItem, loadTableData: LoadTableDataInterface) => {
            openConfirmAlert("Sei sicuro di voler eliminare il prodotto?", () => {
                const [apiCall] = deleteProduct({ id: row.id });

                apiCall
                    .then(() => {
                        enqueueSuccessSnackbar("Prodotto eliminato con successo");

                        loadTableData();
                    })
                    .catch(() => enqueueErrorSnackbar("Errore nell'eliminazione del prodotto"));
            });
        },
        [enqueueSuccessSnackbar, enqueueErrorSnackbar, openConfirmAlert, deleteProduct],
    );

    const duplicateProductButton = React.useCallback(
        (row: ProductListItem, loadTableData: LoadTableDataInterface) => {
            const [apiCall] = duplicateProduct({ id: row.id }, null);

            apiCall
                .then(() => {
                    enqueueSuccessSnackbar("Prodotto duplicato con successo");

                    loadTableData();
                })
                .catch(() => enqueueErrorSnackbar("Errore nella duplicazione del prodotto"));
        },
        [enqueueSuccessSnackbar, enqueueErrorSnackbar, duplicateProduct],
    );

    const columns: Array<Column<ProductListItem>> = React.useMemo(
        () => [
            {
                id: "products.name",
                header: "Nome",
                render: (row: ProductListItem) => row.name,
                orderable: true,
            },
            {
                id: "categories_language.name",
                header: "Categoria",
                render: (row: ProductListItem) => row.category_name,
                orderable: true,
            },
            {
                id: "categories.type",
                header: "Tipologia",
                render: (row: ProductListItem) => row.type,
                orderable: true,
            },
            {
                id: "front_visible",
                header: "Area pubblica",
                render: (row: ProductListItem) => (row.front_visible ? "Visibile" : "Non visibile"),
            },
            {
                id: "reserved_area_visible",
                header: "Area riservata",
                render: (row: ProductListItem) =>
                    row.reserved_area_visible ? "Visibile" : "Non visibile",
            },
            {
                id: "products.is_new",
                header: "Novità",
                render: (row: ProductListItem) => (row.is_new ? "Si" : "No"),
                orderable: true,
            },
            {
                id: "action",
                header: "Azioni",
                headerAlign: "center",
                align: "center",
                width: "100px",
                render: (row: ProductListItem) => (
                    <ActionsTableCell
                        row={row}
                        onEdit={onEditClick}
                        onDelete={deleteProductButton}
                        onDuplicate={duplicateProductButton}
                    />
                ),
            },
        ],
        [onEditClick, deleteProductButton, duplicateProductButton],
    );

    const extraCallParams = React.useMemo(
        () => ({
            sortAdvanced: JSON.stringify([
                { field: "categories.type", direction: "DESC" },
                { field: "categories.graphical_order", direction: "ASC" },
                { field: "products.name", direction: "ASC" },
            ]),
        }),
        [],
    );

    const dataTableHook = useDataTableWithMemory({
        apiCall: getAllProduct,
        getElements: calculateRows,
        extraCallParams: extraCallParams,
        columns: columns,
        filters: filters,
        size: 20,
        key: "product-list",
    });

    return React.useMemo(
        (): ProductsListHook => ({
            onSearchFilterChange,
            navigateToNewProduct,
            staticFilterHook,
            filterSearchHook,
            alertConfirmHook,
            dataTableHook,
        }),
        [
            onSearchFilterChange,
            navigateToNewProduct,
            staticFilterHook,
            filterSearchHook,
            alertConfirmHook,
            dataTableHook,
        ],
    );
}

export default useProductsList;
